/**
 * Noticing that a session is over, wherever the player happens to be looking.
 *
 * This belongs to the shell rather than to a mode. The shell owns the one transition between the
 * menu and a running game, so it is the thing that can act on a session ending, and putting the
 * knowledge in a mode would mean writing it again in the second mode and the third.
 *
 * Two signals, because they are two different failures. A host that leaves on purpose says so on
 * the reliable channel and the answer is instant. A host whose laptop shut is heard from nobody,
 * and the connection watcher works it out about eight seconds later. Whichever arrives first
 * ends the session, and the second one is ignored.
 *
 * The exit is deferred by a microtask. It fires from inside a transport callback, and the teardown
 * it triggers detaches the very listener that is running, over a stop sequence whose order is
 * load-bearing. Letting the callback return first costs nothing and avoids reentering it.
 */

import { decodeLobby } from '@/net/wire/lobbyFrame';
import { MESSAGE_KINDS } from '@/net/wire/messageKinds';
import { netErrorAdvice, netErrorMessage } from '@/net/session/sessionErrors';
import { silentListeners } from '@/net/transportContract';
import type { ExitReason } from '@/modes/modeContract';
import type { NetErrorCode } from '@/net/session/sessionErrors';
import type { NetSession } from '@/net/session/netSession';

/**
 * Watches a live session and calls `onEnded` at most once. Returns the function that stops it.
 *
 * A host is watching nobody: it is the one that ends sessions, and it cannot lose itself.
 */
export function watchSessionEnd(
  session: NetSession,
  onEnded: (reason: ExitReason) => void,
): () => void {
  if (session.role === 'host') return (): void => undefined;

  let ended = false;

  const end = (code: NetErrorCode): void => {
    if (ended) return;
    ended = true;
    queueMicrotask(() => {
      onEnded({ code, message: netErrorMessage(code), advice: netErrorAdvice(code) ?? '' });
    });
  };

  return session.listenTo({
    ...silentListeners(),

    onStatus: (status): void => {
      if (status.code === 'hostLeft') end('hostLeft');
    },

    onMessage: (from, frame): void => {
      if (frame.kind !== MESSAGE_KINDS.lobby || from !== session.hostId()) return;
      const message = decodeLobby(frame.payload);
      if (message.kind === 'ending') end(message.reason);
    },
  });
}
